import { useEffect, useMemo, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import type { AtualizarPessoaDto, CriarPessoaDto, ParceiroResumoResponse, PessoaResponse } from '@triade/contracts';
import { ROTULO_PAPEL_PARCEIRO } from '@triade/contracts';
import { useAnaliticos } from '@/api/centros-custo';
import { useAtualizarPessoa, useCriarPessoa, usePessoa, usePessoas } from '@/api/pessoas';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { mascaraDocumento } from '@/components/fornecedores/documento';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { ParceiroBlocos } from '@/components/parceiro';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { useRolarAoRegistro } from '@/hooks/use-rolar-ao-registro';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { formatarFracaoPercentual } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { BotaoRelatorios } from '@/relatorios';
import { AVISO_DEMISSAO, dePessoa, NATUREZA_ROTULO, PAPEIS_DA_PESSOA, paraAtualizar, paraCriar, pessoaFormSchema, pessoaFormVazio, TURNO_ROTULO, type PessoaFormEntrada, type PessoaFormSaida } from './pessoa-form';
import { CodigosExternos } from '@/components/externos/codigos-externos';
import { ParametrosPessoa } from './pessoa-parametros';

export type SituacaoPessoas = 'ATIVOS' | 'INATIVOS' | 'TODOS';
const SITUACOES: Record<SituacaoPessoas, string> = { ATIVOS: 'Ativos', INATIVOS: 'Inativos', TODOS: 'Todos' };
type Aba = 'dados' | 'parametros';

/** Estado da tela na URL: busca, situacao e a pessoa aberta (`id=novo` abre o cadastro em branco). Chaves prefixadas para nao colidir com outras rotas. */
export interface BuscaPessoas {
  q?: string;
  psituacao?: Exclude<SituacaoPessoas, 'ATIVOS'>;
  id?: string;
}

/** `validateSearch`: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBuscaPessoas(bruto: Record<string, unknown>): BuscaPessoas {
  const texto = typeof bruto.q === 'number' ? String(bruto.q) : bruto.q;
  const q = typeof texto === 'string' && texto.trim() ? texto.trim().slice(0, 100) : undefined;
  const psituacao = bruto.psituacao === 'INATIVOS' || bruto.psituacao === 'TODOS' ? bruto.psituacao : undefined;
  const id = typeof bruto.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.id) ? bruto.id : undefined;
  return {
    ...(q ? { q } : {}),
    ...(psituacao ? { psituacao } : {}),
    ...(id ? { id } : {}),
  };
}

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');
const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

/** Tela "Pessoas" (/cadastros/pessoas): lista com busca e situacao, e registro com abas Dados, Enderecos, Contatos e Parametros de trabalho. */
export function PessoasPage({ busca }: { busca: BuscaPessoas }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('rh.editar');
  const situacao = busca.psituacao ?? 'ATIVOS';
  const [centroId, setCentroId] = useState('');
  const centros = useAnaliticos();

  /** `semBloqueio`: so a sincronizacao do `q` do debounce (troca o parametro da mesma tela, nada se perde); o resto segue o bloqueio. */
  const mudar = (mudanca: Partial<BuscaPessoas>, opcoes?: { semBloqueio?: boolean }) => {
    void navigate({ to: '/cadastros/pessoas', search: (anterior) => ({ ...anterior, ...mudanca }), replace: true, ...(opcoes?.semBloqueio ? { ignoreBlocker: true } : {}) });
  };
  const [termo, setTermo] = useState(busca.q ?? '');
  const termoAtrasado = useDebounced(termo.trim(), 300);
  useEffect(() => {
    if (termoAtrasado !== (busca.q ?? '')) mudar({ q: termoAtrasado || undefined }, { semBloqueio: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAtrasado]);

  const lista = usePessoas({
    ...(termoAtrasado ? { q: termoAtrasado } : {}),
    ...(situacao === 'TODOS' ? {} : { ativo: situacao === 'ATIVOS' }),
    ...(centroId ? { centroId } : {}),
    limit: 200,
  });
  const pessoas = lista.data ?? [];
  const pagPessoas = usePaginarLista(pessoas);

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver as pessoas.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar as pessoas" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando pessoas…</Mensagem>;
  else if (pessoas.length === 0) {
    const filtrado = Boolean(busca.q) || situacao !== 'ATIVOS' || centroId !== '';
    corpo = filtrado ? (
      <Mensagem papel="status">
        Nenhuma pessoa com esses filtros.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => { setCentroId(''); void navigate({ to: '/cadastros/pessoas', search: {}, replace: true }); }}>
          Limpar filtros
        </button>
      </Mensagem>
    ) : (
      <Mensagem papel="status">
        Nenhuma pessoa cadastrada.
        {podeEditar && (
          <>
            {' '}
            <Link to="/cadastros/pessoas" search={{ id: 'novo' }} className="font-medium text-primary hover:underline">
              Cadastre a primeira
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Nome</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Natureza</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Departamento</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Turno</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Cargo (vigente)</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Centro(s) de custo</th>
              <th scope="col" className="px-3 py-2.5 font-medium">CPF/CNPJ</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Situação</th>
            </tr>
          </thead>
          <tbody>
            {pagPessoas.visiveis.map((p) => (
              <tr
                key={p.id}
                data-testid="linha-pessoa"
                onClick={() => void navigate({ to: '/cadastros/pessoas', search: (a) => ({ ...a, id: p.id }) })}
                className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', busca.id === p.id && 'bg-line-soft')}
              >
                <td className="px-4 py-3 font-semibold">{p.nome}</td>
                <td className="px-3 py-3 text-ink-secondary">{NATUREZA_ROTULO[p.natureza]}</td>
                <td className="px-3 py-3 text-ink-secondary">{p.departamento ?? '—'}</td>
                <td className="px-3 py-3 text-ink-secondary">{p.turno ? TURNO_ROTULO[p.turno] : '—'}</td>
                <td className="px-3 py-3 text-ink-secondary">{p.parametroVigente?.cargo.nome ?? '—'}</td>
                <td className="px-3 py-3 text-ink-secondary">
                  <CentrosCusto linhas={p.parametroVigente?.apropriacao?.linhas} />
                </td>
                <td className="tabular px-3 py-3 text-ink-secondary">{p.documento ? mascaraDocumento(p.documento) : '—'}</td>
                <td className="px-4 py-3">
                  <span className={cn('rounded-full px-2 py-0.5 text-caption font-semibold', p.ativo ? 'bg-success-bg text-success-ink' : 'bg-line-soft text-ink-body')}>
                    {p.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Cadastros <span aria-hidden="true">/</span> Pessoas
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Pessoas</h1>
        </div>
      </div>

      <section aria-label="Lista de pessoas" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5">
          <div className="flex h-[34px] w-[260px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
            <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
            <label htmlFor="pes-q" className="sr-only">
              Buscar pessoa
            </label>
            <input id="pes-q" type="search" value={termo} onChange={(e) => setTermo(e.target.value)} placeholder="Nome, identificador ou documento" autoComplete="off" className="w-full min-w-0 bg-transparent text-small outline-none" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="pes-centro" className="text-small text-ink-secondary">
              Centro de custo
            </label>
            <select id="pes-centro" value={centroId} onChange={(e) => setCentroId(e.target.value)} className={CAMPO}>
              <option value="">Todos os centros</option>
              {(centros.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {`${c.caminho} — ${c.nome}`}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="pes-situacao" className="text-small text-ink-secondary">
              Situação
            </label>
            <select id="pes-situacao" value={situacao} onChange={(e) => mudar({ psituacao: e.target.value === 'ATIVOS' ? undefined : (e.target.value as 'INATIVOS' | 'TODOS') })} className={CAMPO}>
              {(Object.keys(SITUACOES) as SituacaoPessoas[]).map((s) => (
                <option key={s} value={s}>
                  {SITUACOES[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        {corpo}
      </section>

      {busca.id && <PessoaDetalhe key={busca.id} id={busca.id === 'novo' ? undefined : busca.id} busca={busca} podeEditar={podeEditar} />}

      {/* Lista: Novo, total e Relatorios na barra. Some com o registro aberto (ele tem a propria RecordToolbar). */}
      {!busca.id && lista.data && (
        <BarraDeAcoesLista
          novo={podeEditar ? { rotulo: 'Nova pessoa', onClick: () => void navigate({ to: '/cadastros/pessoas', search: { ...busca, id: 'novo' } }) } : undefined}
          paginacao={pagPessoas.paginacao}
          relatorios={<BotaoRelatorios tela="pessoas" params={{ q: busca.q }} />}
        />
      )}
    </div>
  );
}

/** Centros da apropriacao vigente: ate 2 inline ("Prensagem 50,00% · Manutenção 50,00%"); acima disso, 2 + "+N" com tooltip listando todos. Percentual so formatado (fracao da API). */
function CentrosCusto({ linhas }: { linhas: { centroId: string; nome: string; percentual: string }[] | undefined }) {
  if (!linhas || linhas.length === 0) return <>—</>;
  const textos = linhas.map((l) => ({ id: l.centroId, texto: `${l.nome} ${formatarFracaoPercentual(l.percentual)}` }));
  const inline = textos.slice(0, 2).map((t) => t.texto).join(' · ');
  if (textos.length <= 2) return <>{inline}</>;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="cursor-help">
            {inline} <span className="font-medium text-primary">+{textos.length - 2}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <ul className="grid gap-0.5">
            {textos.map((t) => (
              <li key={t.id}>{t.texto}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Registro de uma pessoa (ou novo). Abas: Dados, Enderecos, Contatos (um formulario, uma barra) e Parametros de trabalho (vigencias). */
function PessoaDetalhe({ id, busca, podeEditar }: { id: string | undefined; busca: BuscaPessoas; podeEditar: boolean }) {
  const navigate = useNavigate();
  const doc = usePessoa(id);
  const criar = useCriarPessoa();
  const rolagem = useRolarAoRegistro<HTMLElement>();
  const atualizar = useAtualizarPessoa();
  const [aba, setAba] = useState<Aba>('dados');
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [criadoId, setCriadoId] = useState<string | null>(null);
  const base = useMemo<PessoaFormEntrada>(() => (doc.data ? dePessoa(doc.data) : pessoaFormVazio()), [doc.data]);
  const form = useForm<PessoaFormEntrada, unknown, PessoaFormSaida>({ resolver: zodResolver(pessoaFormSchema), defaultValues: base, mode: 'onTouched' });
  const { isDirty, dirtyFields, errors } = form.formState;

  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const bloqueio = useBlockerDirty(isDirty && criadoId === null);
  useEffect(() => {
    if (criadoId) void navigate({ to: '/cadastros/pessoas', search: (a) => ({ ...a, id: criadoId }), replace: true });
  }, [criadoId, navigate]);
  const salvando = criar.isPending || atualizar.isPending;
  const alterados = Object.keys(dirtyFields).length;
  const lista = { to: '/cadastros/pessoas' as const, search: { ...(busca.q ? { q: busca.q } : {}), ...(busca.psituacao ? { psituacao: busca.psituacao } : {}) } };

  async function gravar(v: PessoaFormSaida) {
    setErroSalvar(null);
    try {
      if (id) {
        const montado = paraAtualizar(v, new Set(Object.keys(dirtyFields)));
        if (!montado.ok) return setErroSalvar(montado.mensagem);
        const corpo: AtualizarPessoaDto = montado.dto;
        const gravado = await atualizar.mutateAsync({ id, ...corpo });
        form.reset(dePessoa(gravado));
      } else {
        const montado = paraCriar(v);
        if (!montado.ok) return setErroSalvar(montado.mensagem);
        const corpo: CriarPessoaDto = montado.dto;
        const criado: PessoaResponse = await criar.mutateAsync(corpo);
        form.reset(dePessoa(criado));
        setSalvoEm(new Date());
        setCriadoId(criado.id);
        return;
      }
      setSalvoEm(new Date());
    } catch (e) {
      setErroSalvar(mensagemApi(e));
      if (e instanceof ApiError) {
        for (const i of e.body?.issues ?? []) {
          const campo = i.path.split('.')[0] as keyof PessoaFormEntrada;
          if (campo === 'nome' || campo === 'documento' || campo === 'nascimento') form.setError(campo, { message: i.message });
        }
      }
    }
  }

  /** CPF/CNPJ ja cadastrado: abre a pessoa que ja existe, ou cria a pessoa sobre o parceiro existente (o nome e ignorado pela API nesse caso). */
  async function usarExistente(resumo: ParceiroResumoResponse) {
    const pessoaExistente = resumo.pessoaId;
    if (pessoaExistente) {
      void navigate({ to: '/cadastros/pessoas', search: (a) => ({ ...a, id: pessoaExistente }), ignoreBlocker: true });
      return;
    }
    setErroSalvar(null);
    try {
      const criado = await criar.mutateAsync({ parceiroId: resumo.id, natureza: resumo.natureza, nome: resumo.nome });
      setSalvoEm(new Date());
      setCriadoId(criado.id);
    } catch (e) {
      setErroSalvar(mensagemApi(e));
    }
  }

  if (id && doc.isError) return <section className="rounded-2xl border border-border bg-card p-5"><ErroLista erro={doc.error} onTentar={() => void doc.refetch()} titulo="Não foi possível abrir a pessoa" /></section>;
  if (id && !doc.data) return <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-muted" role="status">Carregando pessoa…</section>;

  const pessoa = doc.data;
  const semParametros = !pessoa;
  const papeisDaPessoa = (pessoa?.papeis ?? []).filter((p) => (PAPEIS_DA_PESSOA as readonly string[]).includes(p));

  return (
    <>
      <section ref={rolagem} aria-label="Dados da pessoa" className="rounded-2xl border border-border bg-card p-5">
        <h2 className="text-body font-semibold">{pessoa ? pessoa.nome : 'Nova pessoa'}</h2>
        {!podeEditar && (
          <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Somente leitura.</b> Seu acesso permite consultar as pessoas, não alterá-las.
          </p>
        )}
        <Tabs value={aba} onValueChange={(v) => setAba(v as Aba)} className="mt-4">
          <TabsList aria-label="Seções da pessoa">
            <TabsTrigger value="dados">Dados</TabsTrigger>
            <TabsTrigger value="parametros" disabled={semParametros}>
              Parâmetros de trabalho
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dados" className="mt-4 flex flex-col gap-6">
            {papeisDaPessoa.length > 0 && (
              <div className="flex flex-wrap items-center gap-2" aria-label="Papéis do parceiro">
                {papeisDaPessoa.map((p) => (
                  <span key={p} className="rounded-full bg-line-soft px-2.5 py-0.5 text-caption font-semibold text-ink-body">
                    {ROTULO_PAPEL_PARCEIRO[p]}
                  </span>
                ))}
              </div>
            )}

            <fieldset disabled={!podeEditar} className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
              <legend className="mb-2 text-small font-semibold text-ink">Dados do colaborador</legend>
              <div>
                <label htmlFor="p-identificador" className={ROTULO}>Identificador (matrícula, código)</label>
                <input id="p-identificador" maxLength={50} className={`${CAMPO} w-full`} {...form.register('identificador')} />
              </div>
              <div>
                <label htmlFor="p-nascimento" className={ROTULO}>Nascimento</label>
                <input id="p-nascimento" type="date" className={`${CAMPO} w-full`} aria-invalid={errors.nascimento ? true : undefined} {...form.register('nascimento')} />
                <MensagemCampo id="p-nascimento-erro" mensagem={errors.nascimento?.message} />
              </div>
              <div>
                <label htmlFor="p-departamento" className={ROTULO}>Departamento</label>
                <input id="p-departamento" maxLength={100} className={`${CAMPO} w-full`} aria-invalid={errors.departamento ? true : undefined} aria-describedby={errors.departamento ? 'p-departamento-erro' : undefined} {...form.register('departamento')} />
                <MensagemCampo id="p-departamento-erro" mensagem={errors.departamento?.message} />
              </div>
              <div>
                <label htmlFor="p-turno" className={ROTULO}>Turno</label>
                <select id="p-turno" className={`${CAMPO} w-full`} {...form.register('turno')}>
                  {(Object.keys(TURNO_ROTULO) as (keyof typeof TURNO_ROTULO)[]).map((t) => (
                    <option key={t || 'nenhum'} value={t}>
                      {TURNO_ROTULO[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="p-admissao" className={ROTULO}>Admissão</label>
                <input id="p-admissao" type="date" className={`${CAMPO} w-full`} aria-invalid={errors.admissao ? true : undefined} {...form.register('admissao')} />
                <MensagemCampo id="p-admissao-erro" mensagem={errors.admissao?.message} />
              </div>
              <div>
                <label htmlFor="p-demissao" className={ROTULO}>Demissão</label>
                <input id="p-demissao" type="date" className={`${CAMPO} w-full`} aria-invalid={errors.demissao ? true : undefined} aria-describedby="p-demissao-aviso" {...form.register('demissao')} />
                <MensagemCampo id="p-demissao-erro" mensagem={errors.demissao?.message} />
                {form.watch('demissao') && (
                  <p id="p-demissao-aviso" className="mt-1 text-caption text-ink-muted">
                    {AVISO_DEMISSAO}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="p-descricao" className={ROTULO}>Descrição</label>
                <input id="p-descricao" maxLength={500} className={`${CAMPO} w-full`} {...form.register('descricao')} />
              </div>
              <label htmlFor="p-ativo" className="flex cursor-pointer items-center gap-2 self-end">
                <input id="p-ativo" type="checkbox" disabled={!id} className="size-[18px] accent-primary" {...form.register('ativo')} />
                Ativo
                {!id && <span className="text-small text-ink-muted">Toda pessoa nova entra ativa.</span>}
              </label>
            </fieldset>

            <ParceiroBlocos
              form={form}
              papel="COLABORADOR"
              parceiroId={pessoa?.parceiroId}
              onUsarExistente={(resumo) => void usarExistente(resumo)}
              desabilitado={!podeEditar}
            />
          </TabsContent>

          <TabsContent value="parametros" className="mt-4">
            {pessoa ? (
              <ParametrosPessoa key={pessoa.id} pessoa={pessoa} podeEditar={podeEditar} />
            ) : (
              <p className="text-small text-ink-muted">Salve a pessoa antes de cadastrar os parâmetros de trabalho.</p>
            )}
          </TabsContent>
        </Tabs>
        {pessoa && (
          <div className="mt-5 border-t border-border pt-4">
            <CodigosExternos entidade="pessoas" id={pessoa.id} editavel={podeEditar} />
          </div>
        )}
        <p className="mt-4 text-caption text-ink-muted">
          Dados de RH (salário, CPF e nascimento) só aparecem para quem tem permissão de RH. CPF e CNPJ são validados pelo dígito verificador.
        </p>
      </section>

      {podeEditar && (
        <RecordToolbar
          dirty={isDirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erroSalvar}
          novo={!id}
          canPrev={false}
          canNext={false}
          alteradosCount={isDirty ? alterados : undefined}
          onList={() => void navigate({ ...lista, replace: true })}
          onSave={() => void form.handleSubmit(gravar, (e: FieldErrors<PessoaFormEntrada>) => setErroSalvar(e.nome?.message ?? 'Corrija os campos destacados'))()}
          onNew={() => void navigate({ to: '/cadastros/pessoas', search: (a) => ({ ...a, id: 'novo' }) })}
          onUndo={() => {
            form.reset(base);
            setErroSalvar(null);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}

